import moment from 'moment';
import { groupBy } from 'lodash';

import { DISPATCHER_CONSTANTS } from './dispatcher_constants';
import {
  DispatcherData,
  DispatcherDispatchRecord,
  DispatcherLayout,
  DispatcherOrderGrouping,
  DispatcherOrderRecord,
  DispatcherOrderSection,
  DispatcherStaffing,
  DispatcherUndispatched,
} from './dispatcher_types';

const chronologicalComparator = (a: DispatcherOrderRecord, b: DispatcherOrderRecord) => {
  const aOffset = moment.duration(a.dispatch_offset ?? 0, DISPATCHER_CONSTANTS.DISPATCHER_OFFSET_UNITS);
  const bOffset = moment.duration(b.dispatch_offset ?? 0, DISPATCHER_CONSTANTS.DISPATCHER_OFFSET_UNITS);

  const aScheduled = moment(moment.tz(a.scheduled, a.region.tz).valueOf() + aOffset.asMilliseconds());
  const bScheduled = moment(moment.tz(b.scheduled, b.region.tz).valueOf() + bOffset.asMilliseconds());

  const timeDiff =
    aScheduled.hour() * 3600 + aScheduled.minute() * 60 - (bScheduled.hour() * 3600 + bScheduled.minute() * 60);

  if (timeDiff !== 0) {
    return timeDiff;
  }

  const nameDiff = (a.account.customers[0]?.name ?? '').localeCompare(b.account.customers[0]?.name ?? '');
  if (nameDiff !== 0) {
    return nameDiff;
  }

  return a.id - b.id;
};

const sortChronologically = (orders: DispatcherOrderRecord[]) => [...orders].sort(chronologicalComparator);

const sectionOrdersByMovers = (orders: DispatcherOrderRecord[]): DispatcherOrderSection[] => {
  const groupings = groupBy(orders, (order) => order.movers ?? 1);

  return Object.keys(groupings)
    .map((section) => ({
      section: Number(section),
      resources: groupings[section],
    }))
    .sort((a, b) => b.section - a.section);
};

const maxMoverCount = (dispatch: DispatcherDispatchRecord) => {
  if (dispatch.orders.length === 0) {
    return 1;
  }

  return Math.max(...dispatch.orders.map((order) => order.movers || 1));
};

export const calculateStaffing = (
  dispatches: DispatcherDispatchRecord[],
  fieldShiftAssignments: DispatcherData['fieldShiftAssignments'],
): DispatcherStaffing => {
  const activeDispatches = dispatches.filter((dispatch) => !dispatch._destroy);

  const driversRequired = activeDispatches.reduce((total, dispatch) => {
    const moverCount = Math.max(maxMoverCount(dispatch), 1);
    return total + (moverCount >= 4 ? 2 : 1);
  }, 0);

  const nonDriversRequired = activeDispatches.reduce((total, dispatch) => {
    const moverCount = Math.max(maxMoverCount(dispatch), 1);
    return total + (moverCount >= 4 ? moverCount - 2 : moverCount - 1);
  }, 0);

  return {
    driverShiftCount: fieldShiftAssignments.filter((assignment) => assignment.qualified_driver).length,
    nonDriverShiftCount: fieldShiftAssignments.filter((assignment) => !assignment.qualified_driver).length,
    driversRequired,
    nonDriversRequired,
  };
};

export const buildUndispatched = (
  orders: DispatcherOrderRecord[],
  dispatches: DispatcherDispatchRecord[],
): DispatcherUndispatched => {
  const activeDispatches = dispatches.filter((dispatch) => !dispatch._destroy);
  const placedOrderIDs = new Set(activeDispatches.flatMap((dispatch) => dispatch.orders.map((order) => order.id)));

  const undispatchedOrders = sortChronologically(
    orders
      .filter((order) => !placedOrderIDs.has(order.id))
      .map((order) => ({
        ...order,
        rescheduled: !!order.dispatch_id && !dispatches.some((dispatch) => dispatch.id === order.dispatch_id),
      })),
  );

  const parentOrdersAndRestocks = undispatchedOrders.filter((order) => !order.parent_id || order.type === 'restock');

  return {
    orders: parentOrdersAndRestocks,
    sectioned: sectionOrdersByMovers(parentOrdersAndRestocks),
  };
};

export const buildOrderGroupings = (orders: DispatcherOrderRecord[]): DispatcherOrderGrouping[] => {
  const relevantOrders = orders.filter((order) => !order.parent_id || order.type === 'restock');
  const sorted = sortChronologically(relevantOrders);

  const groups: DispatcherOrderGrouping[] = [];
  let group: DispatcherOrderGrouping | null = null;

  sorted.forEach((order) => {
    const duration = moment.duration(
      order.estimated_duration || DISPATCHER_CONSTANTS.DEFAULT_ESTIMATED_DURATION,
      DISPATCHER_CONSTANTS.ESTIMATED_DURATION_UNITS,
    );
    const offset = moment.duration(order.dispatch_offset ?? 0, DISPATCHER_CONSTANTS.DISPATCHER_OFFSET_UNITS);
    const scheduled = moment(moment.tz(order.scheduled, order.region.tz).valueOf() + offset.asMilliseconds());
    const from = scheduled.hour() * 3600 + scheduled.minute() * 60;
    const till = from + duration.asSeconds();

    if (!group || group.from >= till || group.till <= from) {
      group = { from, till, orders: [order] };
      groups.push(group);
      return;
    }

    group.orders.push(order);
    if (from < group.from) {
      group.from = from;
    }
    if (till > group.till) {
      group.till = till;
    }
  });

  return groups;
};

export const updateDispatchGroupings = (dispatches: DispatcherDispatchRecord[]) => {
  dispatches.forEach((dispatch) => {
    dispatch.groupings = buildOrderGroupings(dispatch.orders);
  });
};

/** @deprecated Use updateDispatchGroupings — kept for call sites that expect a return value. */
export const applyDispatchGroupings = (dispatches: DispatcherDispatchRecord[]): DispatcherDispatchRecord[] => {
  updateDispatchGroupings(dispatches);
  return dispatches;
};

export const buildDispatcherLayout = (data: DispatcherData): DispatcherLayout => {
  updateDispatchGroupings(data.dispatches);

  return {
    undispatched: buildUndispatched(data.orders, data.dispatches),
    staffing: calculateStaffing(data.dispatches, data.fieldShiftAssignments),
    dispatches: data.dispatches.filter((dispatch) => !dispatch._destroy),
  };
};
